import { type FC, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import clsx from 'clsx';
import isEqual from 'lodash/isEqual';

import { Chart } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/Chart';
import { StatusBar } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/StatusChart/components/StatusBar';
import { StatusChartFilter } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/components/StatusChart/components/StatusChartFilter';
import { useStatusChartData } from '@/app/[locale]/(authorized)/(dashboard)/components/Dashboard/components/Charts/hooks/use-status-chart-data';
import { type GateGroupDetails } from '@/app/[locale]/(authorized)/payment-gates/[id]/components/PaymentGate/hooks/use-gate-group-details';
import { Flex } from '@/components/common/Flex';
import { Spinner } from '@/components/common/Spinner';
import { type WithClassName } from '@/types/react';
import { isDefined } from '@/utils/utils';

import { type StatusChartOperationType, TransactionStatus } from '../../../../../../../../../../../generated/graphql';

import styles from './StatusChart.module.scss';

type StatusChartProps = WithClassName & {
  title: ReactNode;
  defaultCurrency: {
    id: string;
    name: string;
  };
  operationType: StatusChartOperationType;
  gateGroupDetails?: GateGroupDetails;
};

const STATUS_ORDER: TransactionStatus[] = [
  TransactionStatus.Succeed,
  TransactionStatus.Failed,
  TransactionStatus.Pending,
  TransactionStatus.Created,
];

export const StatusChart: FC<StatusChartProps> = (props) => {
  const { className, title, defaultCurrency, operationType, gateGroupDetails } = props;

  const [filter, setFilter] = useState<StatusChartFilter | undefined>();

  const defaultFormValues = useMemo(() => {
    if (!gateGroupDetails) {
      return {
        operationType,
      };
    }

    return {
      operationType,
      currency: defaultCurrency,
      gateGroup: {
        id: gateGroupDetails.id,
        name: gateGroupDetails.name ?? gateGroupDetails.id,
        currency_id: gateGroupDetails.currency_id,
      },
    };
  }, [defaultCurrency, gateGroupDetails, operationType]);

  const {
    data,
    loading,
    refetch: refetchData,
  } = useStatusChartData({
    filter,
  });

  const chartData = data?.dashboardWidgetsStatusChartV2.data;

  const hasTransactions = useMemo(() => chartData?.some(({ count }) => count > 0) ?? false, [chartData]);

  const statusRows = useMemo(() => {
    const entries = chartData ?? [];
    const percentByStatus = new Map(entries.map((entry) => [entry.status, entry.percent]));

    return STATUS_ORDER.map((status) => ({
      status,
      percent: percentByStatus.get(status) ?? 0,
    }));
  }, [chartData]);

  const onFilter = useCallback<SubmitHandler<StatusChartFilter>>(
    (data) => {
      setFilter(data);

      // Если ничего не поменялось, аполло сам не перезапустит query
      if (isEqual(data, filter)) {
        refetchData();
      }
    },
    [filter, refetchData]
  );

  return (
    <Chart
      className={clsx(styles.root, className)}
      title={title}
      filter={
        <StatusChartFilter
          defaultValues={filter ?? defaultFormValues}
          isInGate={isDefined(gateGroupDetails)}
          onSubmit={onFilter}
        />
      }
      chart={
        loading ? (
          <Flex alignItems="center" justifyContent="center">
            <Spinner size={56} />
          </Flex>
        ) : (
          <Flex className={clsx(!hasTransactions && styles.emptyState)} direction="column" gap={4}>
            {statusRows.map(({ status, percent }) => (
              <StatusBar key={status} status={status} percent={percent} />
            ))}
          </Flex>
        )
      }
    />
  );
};
